import React, { useState, useEffect } from 'react';
import { Bot, Save, RotateCcw, Palette, Check, Sparkles, Power } from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { sanitizeHexColor, cn } from '@/lib/utils';
import { useBillingStore } from '@/stores/useBillingStore';
import { UPGRADE_MODAL_COPY } from '@/features/billing';
import type { StorePreferences, UpdateStorePreferencesCommand } from '@/types';

const COLOR_PRESETS = [
  { name: 'Classic Blue', primary: '#2563eb', secondary: '#64748b' },
  { name: 'Indigo Aura', primary: '#4f46e5', secondary: '#94a3b8' },
  { name: 'Violet Nova', primary: '#7c3aed', secondary: '#94a3b8' },
  { name: 'Emerald Fresh', primary: '#059669', secondary: '#475569' },
  { name: 'Rose Bloom', primary: '#e11d48', secondary: '#64748b' },
  { name: 'Amber Warmth', primary: '#d97706', secondary: '#475569' },
  { name: 'Zinc Modern', primary: '#18181b', secondary: '#71717a' },
  { name: 'Slate Calm', primary: '#334155', secondary: '#64748b' },
];

const sanitizeHex = sanitizeHexColor;

interface StorePreferencesFormProps {
  storeId?: string;
  initialPreferences?: StorePreferences | null;
  isLoading?: boolean;
  isSaving?: boolean;
  onSave: (command: UpdateStorePreferencesCommand) => void;
  onToggleWidget?: (enabled: boolean) => Promise<void>;
  onLiveChange?: (preferences: StorePreferences) => void;
}

export const StorePreferencesForm: React.FC<StorePreferencesFormProps> = ({
  storeId,
  initialPreferences,
  isLoading = false,
  isSaving = false,
  onSave,
  onToggleWidget,
  onLiveChange,
}) => {
  const openUpgradeModal = useBillingStore((s) => s.openUpgradeModal);
  const [isTogglingWidget, setIsTogglingWidget] = useState(false);

  const [agentName, setAgentName] = useState(initialPreferences?.agentName || 'Nova');
  const [greetingMessage, setGreetingMessage] = useState(
    initialPreferences?.greetingMessage ||
      'Olá! Bem-vindo à nossa loja. Como posso ajudar você hoje?'
  );
  const [widgetTitle, setWidgetTitle] = useState(initialPreferences?.widgetTitle || 'Suporte ao Cliente');
  const [widgetPrimaryColor, setWidgetPrimaryColor] = useState(
    initialPreferences?.widgetPrimaryColor || '#2563eb'
  );
  const [widgetSecondaryColor, setWidgetSecondaryColor] = useState(
    initialPreferences?.widgetSecondaryColor || '#64748b'
  );
  const [systemPrompt, setSystemPrompt] = useState(initialPreferences?.systemPrompt || '');
  const [isWidgetEnabled, setIsWidgetEnabled] = useState<boolean>(
    initialPreferences?.isWidgetEnabled ?? true
  );

  // Backend entitlements:
  // - agentName & greetingMessage: allowed if ChangeGreeting is present (unlocked in Starter & Pro)
  // - widgetPrimaryColor, widgetSecondaryColor, widgetTitle: allowed if CustomWidgetColors is present (unlocked in Pro)
  // - systemPrompt: allowed if CustomizeBehavior is present (unlocked in Pro)
  const billingStatus = useBillingStore((state) => state.status);
  const canChangeGreeting = billingStatus
    ? Boolean(billingStatus.allowedFeatures?.includes('ChangeGreeting'))
    : true;
  const canCustomWidgetColors = billingStatus
    ? Boolean(billingStatus.allowedFeatures?.includes('CustomWidgetColors'))
    : true;
  const canCustomizeBehavior = billingStatus
    ? Boolean(billingStatus.allowedFeatures?.includes('CustomizeBehavior'))
    : true;

  // Sync state when initialPreferences changes (ignore during active user toggle to avoid clobbering)
  useEffect(() => {
    if (initialPreferences && !isTogglingWidget) {
      setAgentName(initialPreferences.agentName || 'Nova');
      setGreetingMessage(
        initialPreferences.greetingMessage ||
          'Olá! Bem-vindo à nossa loja. Como posso ajudar você hoje?'
      );
      setWidgetTitle(initialPreferences.widgetTitle || 'Suporte ao Cliente');
      setWidgetPrimaryColor(initialPreferences.widgetPrimaryColor || '#2563eb');
      setWidgetSecondaryColor(initialPreferences.widgetSecondaryColor || '#64748b');
      setSystemPrompt(initialPreferences.systemPrompt || '');
      setIsWidgetEnabled(initialPreferences.isWidgetEnabled ?? true);
    }
  }, [initialPreferences, isTogglingWidget]);

  // Notify live changes for widget preview
  const notifyChanges = (updated: Partial<StorePreferences>) => {
    const current: StorePreferences = {
      storeId: storeId || initialPreferences?.storeId || '',
      agentName: updated.agentName !== undefined ? updated.agentName : agentName,
      greetingMessage: updated.greetingMessage !== undefined ? updated.greetingMessage : greetingMessage,
      widgetTitle: updated.widgetTitle !== undefined ? updated.widgetTitle : widgetTitle,
      widgetPrimaryColor: updated.widgetPrimaryColor !== undefined ? updated.widgetPrimaryColor : widgetPrimaryColor,
      widgetSecondaryColor: updated.widgetSecondaryColor !== undefined ? updated.widgetSecondaryColor : widgetSecondaryColor,
      systemPrompt: updated.systemPrompt !== undefined ? updated.systemPrompt : systemPrompt,
      isWidgetEnabled: updated.isWidgetEnabled !== undefined ? updated.isWidgetEnabled : isWidgetEnabled,
    };
    onLiveChange?.(current);
  };

  const handleApplyPreset = (preset: { primary: string; secondary: string }) => {
    setWidgetPrimaryColor(preset.primary);
    setWidgetSecondaryColor(preset.secondary);
    notifyChanges({ widgetPrimaryColor: preset.primary, widgetSecondaryColor: preset.secondary });
  };

  const handleResetDefaults = () => {
    const defaults = {
      agentName: 'Nova',
      greetingMessage: 'Olá! Bem-vindo à nossa loja. Como posso ajudar você hoje?',
      widgetTitle: 'Atendimento ao Cliente',
      widgetPrimaryColor: '#2563eb',
      widgetSecondaryColor: '#64748b',
      systemPrompt: null,
      isWidgetEnabled: true,
    };
    setAgentName(defaults.agentName);
    setGreetingMessage(defaults.greetingMessage);
    setWidgetTitle(defaults.widgetTitle);
    setWidgetPrimaryColor(defaults.widgetPrimaryColor);
    setWidgetSecondaryColor(defaults.widgetSecondaryColor);
    setSystemPrompt('');
    setIsWidgetEnabled(true);
    notifyChanges(defaults);
  };

  const handleToggleWidget = async (checked: boolean) => {
    const previousState = isWidgetEnabled;
    setIsWidgetEnabled(checked);
    notifyChanges({ isWidgetEnabled: checked });

    if (onToggleWidget) {
      setIsTogglingWidget(true);
      try {
        await onToggleWidget(checked);
      } catch {
        // Revert on error
        setIsWidgetEnabled(previousState);
        notifyChanges({ isWidgetEnabled: previousState });
      } finally {
        setIsTogglingWidget(false);
      }
    } else {
      // Direct auto-save fallback via onSave
      onSave({
        agentName: agentName.trim(),
        greetingMessage: greetingMessage.trim(),
        widgetTitle: widgetTitle.trim(),
        widgetPrimaryColor: sanitizeHex(widgetPrimaryColor),
        widgetSecondaryColor: sanitizeHex(widgetSecondaryColor),
        systemPrompt: systemPrompt.trim() ? systemPrompt.trim() : null,
        isWidgetEnabled: checked,
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      agentName: agentName.trim(),
      greetingMessage: greetingMessage.trim(),
      widgetTitle: widgetTitle.trim(),
      widgetPrimaryColor: sanitizeHex(widgetPrimaryColor),
      widgetSecondaryColor: sanitizeHex(widgetSecondaryColor),
      systemPrompt: systemPrompt.trim() ? systemPrompt.trim() : null,
      isWidgetEnabled: isWidgetEnabled,
    });
  };

  const handleOpenUpgrade = (feature?: 'greeting' | 'appearance' | 'behavior') => {
    switch (feature) {
      case 'greeting':
        openUpgradeModal(UPGRADE_MODAL_COPY.agent_identity);
        break;
      case 'appearance':
        openUpgradeModal(UPGRADE_MODAL_COPY.agent_branding);
        break;
      case 'behavior':
        openUpgradeModal(UPGRADE_MODAL_COPY.ai_instructions);
        break;
      default:
        openUpgradeModal(UPGRADE_MODAL_COPY.publish_store);
        break;
    }
  };

  // Dynamic non-hardcoded premium feature indicator
  const renderFeatureIndicator = (
    hasFeature: boolean,
    feature: 'greeting' | 'appearance' | 'behavior'
  ) => {
    if (hasFeature) return null;
    return (
      <button
        type="button"
        onClick={() => handleOpenUpgrade(feature)}
        className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary hover:bg-primary/20 cursor-pointer transition-colors shadow-2xs"
        title="Modo de teste ativo. Faça upgrade do seu plano para publicar na sua loja."
        aria-label={`Recurso Premium: Fazer upgrade para desbloquear ${feature}`}
      >
        <Sparkles className="h-2.5 w-2.5" />
        <span>Premium</span>
      </button>
    );
  };

  if (isLoading) {
    return (
      <Card className="p-8 text-center flex flex-col items-center justify-center min-h-[300px] space-y-3">
        <LoadingSpinner size="lg" />
        <p className="text-xs text-muted-foreground">Carregando configurações do Agente de IA...</p>
      </Card>
    );
  }

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* 0. Widget Storefront Activation Status */}
        <Card id="widget-status" className="scroll-mt-6 border-l-4 border-l-primary shadow-xs">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Power className="h-4 w-4 text-primary" />
                    Status do Chat na Loja
                  </CardTitle>
                  <span
                    className={cn(
                      'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium border',
                      isWidgetEnabled
                        ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20'
                        : 'bg-muted text-muted-foreground border-border'
                    )}
                  >
                    <span
                      className={cn(
                        'h-1.5 w-1.5 rounded-full',
                        isWidgetEnabled ? 'bg-emerald-500 animate-pulse' : 'bg-muted-foreground'
                      )}
                    />
                    {isWidgetEnabled ? 'Widget Ativo' : 'Widget Desativado'}
                  </span>
                </div>
                <CardDescription className="text-xs">
                  Controle a exibição do balão de atendimento na sua loja virtual em tempo real
                </CardDescription>
              </div>
              <div className="flex items-center gap-2.5">
                {isTogglingWidget && (
                  <span className="flex items-center gap-1 text-[11px] text-muted-foreground animate-in fade-in">
                    <LoadingSpinner size="sm" />
                    <span className="hidden sm:inline">Salvando...</span>
                  </span>
                )}
                <Switch
                  id="isWidgetEnabled"
                  checked={isWidgetEnabled}
                  disabled={isTogglingWidget || isLoading}
                  onCheckedChange={handleToggleWidget}
                  aria-label="Ativar ou desativar o widget na loja"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0 text-xs text-muted-foreground">
            {isWidgetEnabled ? (
              <p>
                O widget de atendimento está <strong>ativo</strong> na sua loja. Os visitantes conseguem visualizar o balão flutuante e interagir com o Agente de IA.
              </p>
            ) : (
              <p className="text-amber-600 dark:text-amber-400">
                O widget está <strong>desativado</strong>. O balão não será renderizado e não aparecerá para os clientes na loja virtual.
              </p>
            )}
          </CardContent>
        </Card>

        {/* 1. AI Persona Configuration */}
        <Card id="ai-persona" className="scroll-mt-6">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Bot className="h-4 w-4 text-primary" />
                Agente de IA e Saudação
              </CardTitle>
              {!canChangeGreeting && renderFeatureIndicator(false, 'greeting')}
            </div>
            <CardDescription className="text-xs">
              Configure como seu Agente de IA se apresenta aos visitantes da sua loja
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="agentName" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Nome do Agente de IA
                </Label>
                {renderFeatureIndicator(canChangeGreeting, 'greeting')}
              </div>
              <Input
                id="agentName"
                placeholder="ex. Nova, Aura, Athena"
                value={agentName}
                onChange={(e) => {
                  setAgentName(e.target.value);
                  notifyChanges({ agentName: e.target.value });
                }}
                required
                className="h-9 text-xs"
              />
              <p className="text-[11px] text-muted-foreground">
                Nome exibido para o cliente no topo e nas mensagens do chat.
              </p>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="greetingMessage" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Mensagem de Boas-Vindas
                </Label>
                {renderFeatureIndicator(canChangeGreeting, 'greeting')}
              </div>
              <Textarea
                id="greetingMessage"
                placeholder="Mensagem de boas-vindas enviada quando um cliente abre o chat..."
                value={greetingMessage}
                onChange={(e) => {
                  setGreetingMessage(e.target.value);
                  notifyChanges({ greetingMessage: e.target.value });
                }}
                rows={3}
                required
                className="text-xs resize-none"
              />
              <p className="text-[11px] text-muted-foreground">
                Mensagem enviada automaticamente quando o cliente abre o chat da loja.
              </p>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <Label htmlFor="systemPrompt" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                    <span>Instruções e Regras para o Agente de IA</span>
                    <span className="text-[10px] font-normal lowercase tracking-normal text-muted-foreground/80 bg-muted px-1.5 py-0.5 rounded">
                      opcional
                    </span>
                  </Label>
                  {renderFeatureIndicator(canCustomizeBehavior, 'behavior')}
                </div>
                <span
                  className={cn(
                    'text-[11px] font-mono',
                    systemPrompt.length >= 950
                      ? 'text-amber-500 font-semibold'
                      : 'text-muted-foreground'
                  )}
                >
                  {systemPrompt.length} / 1000
                </span>
              </div>
              <Textarea
                id="systemPrompt"
                placeholder={
                  !canCustomizeBehavior
                    ? 'Faça upgrade do seu plano para definir instruções personalizadas, regras da loja ou promoções para o Agente de IA.'
                    : 'ex. Sempre mencione que oferecemos frete grátis para pedidos acima de R$ 50. Seja amigável e prestativo.'
                }
                value={systemPrompt}
                maxLength={1000}
                disabled={!canCustomizeBehavior}
                readOnly={!canCustomizeBehavior}
                onChange={(e) => {
                  setSystemPrompt(e.target.value);
                  notifyChanges({ systemPrompt: e.target.value });
                }}
                rows={5}
                className={cn('text-xs resize-y min-h-[110px]', !canCustomizeBehavior && 'opacity-60 cursor-not-allowed bg-muted/40')}
              />
              <p className="text-[11px] text-muted-foreground">
                {!canCustomizeBehavior
                  ? 'Instruções personalizadas requerem uma assinatura ativa. O comportamento padrão da loja está ativo.'
                  : 'Defina regras da sua loja, promoções ou tom de voz para o Agente de IA. Deixe em branco para usar o padrão da loja.'}
              </p>
            </div>
          </CardContent>
          <CardFooter className="pt-3 border-t border-border/60 flex items-center justify-between">
            <p className="text-[11px] text-muted-foreground">
              Alterações na saudação e no agente se aplicam ao chat da loja.
            </p>
            {canChangeGreeting ? (
              <Button type="submit" size="sm" disabled={isSaving} className="gap-1.5 shadow-sm font-medium">
                {isSaving ? <LoadingSpinner size="sm" /> : <Save className="h-4 w-4" />}
                <span>Salvar Agente de IA</span>
              </Button>
            ) : (
              <Button
                type="button"
                size="sm"
                onClick={() => handleOpenUpgrade('greeting')}
                className="gap-1.5 shadow-sm font-semibold bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                <Sparkles className="h-4 w-4" />
                <span>Publicar na Loja</span>
              </Button>
            )}
          </CardFooter>
        </Card>

        {/* 2. Live Widget Appearance & Branding */}
        <Card id="widget-appearance" className="scroll-mt-6">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Palette className="h-4 w-4 text-primary" />
                Visual do Chat
              </CardTitle>
              {renderFeatureIndicator(canCustomWidgetColors, 'appearance')}
            </div>
            <CardDescription className="text-xs">
              Personalize as cores e o título do chat da sua loja
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="widgetTitle" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Título do Chat
              </Label>
              <Input
                id="widgetTitle"
                placeholder="ex. Suporte ao Cliente, Atendimento"
                value={widgetTitle}
                onChange={(e) => {
                  setWidgetTitle(e.target.value);
                  notifyChanges({ widgetTitle: e.target.value });
                }}
                required
                className="h-9 text-xs"
              />
            </div>

            {/* Color Palettes Preset Bar */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Predefinições de Paleta de Marca
              </Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {COLOR_PRESETS.map((preset) => {
                  const isActive =
                    sanitizeHex(widgetPrimaryColor) === preset.primary.toLowerCase();
                  return (
                    <button
                      key={preset.name}
                      type="button"
                      onClick={() => handleApplyPreset(preset)}
                      className={`flex items-center gap-2 p-2 rounded-lg border text-left text-xs transition-all cursor-pointer ${
                        isActive
                          ? 'border-primary bg-primary/5 ring-1 ring-primary'
                          : 'border-border/60 hover:bg-muted/40'
                      }`}
                    >
                      <div
                        className="h-4 w-4 rounded-full flex-shrink-0 shadow-sm flex items-center justify-center text-white"
                        style={{ backgroundColor: preset.primary }}
                      >
                        {isActive && <Check className="h-2.5 w-2.5" />}
                      </div>
                      <span className="truncate text-[11px] font-medium text-foreground">
                        {preset.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Custom Hex Color Inputs */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
              <div className="space-y-1.5">
                <Label htmlFor="primaryColor" className="text-xs font-medium">
                  Cor Principal da Marca (Cabeçalho e CTAs)
                </Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={sanitizeHex(widgetPrimaryColor)}
                    onChange={(e) => {
                      setWidgetPrimaryColor(e.target.value);
                      notifyChanges({ widgetPrimaryColor: e.target.value });
                    }}
                    className="h-9 w-9 rounded-md border border-input p-0.5 bg-background cursor-pointer"
                  />
                  <Input
                    id="primaryColor"
                    value={widgetPrimaryColor}
                    onChange={(e) => {
                      setWidgetPrimaryColor(e.target.value);
                      notifyChanges({ widgetPrimaryColor: e.target.value });
                    }}
                    placeholder="#2563eb"
                    className="h-9 text-xs font-mono"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="secondaryColor" className="text-xs font-medium">
                  Cor de Destaque Secundária
                </Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={sanitizeHex(widgetSecondaryColor, '#64748b')}
                    onChange={(e) => {
                      setWidgetSecondaryColor(e.target.value);
                      notifyChanges({ widgetSecondaryColor: e.target.value });
                    }}
                    className="h-9 w-9 rounded-md border border-input p-0.5 bg-background cursor-pointer"
                  />
                  <Input
                    id="secondaryColor"
                    value={widgetSecondaryColor}
                    onChange={(e) => {
                      setWidgetSecondaryColor(e.target.value);
                      notifyChanges({ widgetSecondaryColor: e.target.value });
                    }}
                    placeholder="#64748b"
                    className="h-9 text-xs font-mono"
                  />
                </div>
              </div>
            </div>
          </CardContent>

          <CardFooter className="pt-3 border-t border-border/60 flex items-center justify-between">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleResetDefaults}
              className="gap-1.5 text-xs text-muted-foreground hover:text-foreground"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              <span>Redefinir Padrões</span>
            </Button>

            {canCustomWidgetColors ? (
              <Button type="submit" size="sm" disabled={isSaving} className="gap-1.5 shadow-sm font-medium">
                {isSaving ? <LoadingSpinner size="sm" /> : <Save className="h-4 w-4" />}
                <span>Salvar Visual</span>
              </Button>
            ) : (
              <Button
                type="button"
                size="sm"
                onClick={() => handleOpenUpgrade('appearance')}
                className="gap-1.5 shadow-sm font-semibold bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                <Sparkles className="h-4 w-4" />
                <span>Publicar na Loja</span>
              </Button>
            )}
          </CardFooter>
        </Card>
      </form>
    </>
  );
};
